"use client";

import { useEffect } from "react";
import { type Accept, useDropzone } from "react-dropzone";
import { InputInfo } from "./InputInfo";
import { cn } from "@repo/shared/utils";
import { Label } from "./Label";
import { UploadCloudIcon } from "lucide-react";
import { useFutureCallback } from "./hooks/useFutureCallback";

const imageTypes = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  // "image/tiff": [".tif", ".tiff"],
};
const compliantDocTypes = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/tiff": [".tif", ".tiff"],
};
const allFiles = undefined;

const acceptMap = {
  images: imageTypes,
  "compliant-docs": compliantDocTypes,
  all: allFiles,
};

interface FileUploadProps {
  label?: string;
  id?: string;
  disabled?: boolean;
  handleFileUpload?: (file: File) => void;
  error?: boolean;
  errorMessage?: string;
  dropTargetMessage?: string;
  accept: Accept | keyof typeof acceptMap;
  small?: boolean;
  className?: string;
}

export const FileUpload = ({
  label,
  id,
  disabled,
  handleFileUpload: _handleFileUpload,
  error,
  errorMessage,
  dropTargetMessage,
  accept,
  small,
  className,
}: FileUploadProps) => {
  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    acceptedFiles,
  } = useDropzone({
    accept: typeof accept === "string" ? acceptMap[accept] : accept,
    disabled,
  });

  const handleFileUpload = useFutureCallback(_handleFileUpload ?? (() => {}));

  useEffect(() => {
    acceptedFiles.forEach((file) => {
      handleFileUpload(file);
    });
  }, [acceptedFiles, handleFileUpload]);

  return (
    <div className={cn("flex flex-col gap-y-3", className)}>
      {label && <Label label={label} id={id} />}
      <div
        {...getRootProps({
          className: cn(
            "bg-slate-100 flex flex-col px-2 py-8 text-center rounded-md cursor-pointer transistion-all duration-200 items-center",
            disabled && "cursor-not-allowed",
            small && "flex-row px-4 py-2 gap-4 justify-center",
            isFocused
              ? "border-accent border-solid border"
              : isDragAccept
                ? "border-green-600 border-4"
                : isDragReject
                  ? "border-destructive border-4"
                  : "border-slate-700 border-dashed border"
          ),
        })}
      >
        <input
          disabled={disabled}
          {...getInputProps({ id: id || "file-upload-input" })}
        />
        <div className={cn(disabled && "opacity-50")}>
          <UploadCloudIcon size={small ? 32 : 64} />
        </div>
        <div className={cn(disabled && "opacity-50")}>
          {dropTargetMessage ?? "Drag and drop a file or click here to browse"}
        </div>
      </div>
      {error && errorMessage && <InputInfo info={errorMessage} />}
    </div>
  );
};
